import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useKubernetesConsoleApi } from './api';

interface Props {
  cluster: string;
  namespace: string;
  pod: string;
  container: string;
}

export function PodTerminal({ cluster, namespace, pod, container }: Props) {
  const api = useKubernetesConsoleApi();
  const hostRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const sessionRef = useRef<{ id: string; token: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputQueueRef = useRef('');
  const inputTimerRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const flushInput = useCallback(async () => {
    inputTimerRef.current = null;
    const session = sessionRef.current;
    const data = inputQueueRef.current;
    inputQueueRef.current = '';
    if (!session || !data) return;
    try {
      await api.sendExecInput(session.id, session.token, data);
    } catch (sendError) {
      setError(
        sendError instanceof Error ? sendError.message : String(sendError),
      );
    }
  }, [api]);

  const disconnect = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (inputTimerRef.current !== null) {
      window.clearTimeout(inputTimerRef.current);
      inputTimerRef.current = null;
    }
    const session = sessionRef.current;
    sessionRef.current = null;
    setConnected(false);
    if (session) {
      try {
        await api.closeExecSession(session.id, session.token);
      } catch {
        // The session may already have timed out or exited.
      }
    }
  }, [api]);

  const connect = useCallback(async () => {
    if (!hostRef.current || !container) return;
    await disconnect();
    setConnecting(true);
    setError(null);

    const terminal = new Terminal({
      cursorBlink: true,
      convertEol: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 13,
      theme: {
        background: '#141414',
        foreground: '#f0f0f0',
        cursor: '#8bc1f7',
      },
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.open(hostRef.current);
    fit.fit();
    terminal.focus();
    terminalRef.current?.dispose();
    terminalRef.current = terminal;

    try {
      const session = await api.createExecSession(
        cluster,
        namespace,
        pod,
        container,
        { cols: terminal.cols, rows: terminal.rows },
      );
      sessionRef.current = session;
      setConnected(true);
      const abortController = new AbortController();
      abortRef.current = abortController;
      terminal.onData(data => {
        inputQueueRef.current += data;
        if (inputTimerRef.current === null) {
          inputTimerRef.current = window.setTimeout(flushInput, 12);
        }
      });
      // Keep the container's pty in sync with the browser terminal's size —
      // this is what makes `ls` column widths, line-wrapping, and
      // full-screen apps (vim, htop) render correctly instead of assuming a
      // fixed 80x24.
      terminal.onResize(({ cols, rows }) => {
        api
          .resizeExecSession(session.id, session.token, cols, rows)
          .catch(() => undefined);
      });
      const hostElement = hostRef.current;
      if (hostElement) {
        const resizeObserver = new ResizeObserver(() => fit.fit());
        resizeObserver.observe(hostElement);
        resizeObserverRef.current = resizeObserver;
      }
      api
        .streamExecSession(
          session.id,
          session.token,
          event => {
            if (event.type === 'status') {
              terminal.writeln(`\r\n\x1b[33m${event.data}\x1b[0m`);
              setConnected(false);
              abortController.abort();
              abortRef.current = null;
            } else {
              terminal.write(event.data);
            }
          },
          abortController.signal,
        )
        .catch(streamError => {
          if (
            !(streamError instanceof DOMException) ||
            streamError.name !== 'AbortError'
          ) {
            setError(
              streamError instanceof Error
                ? streamError.message
                : String(streamError),
            );
          }
        });
    } catch (connectError) {
      terminal.dispose();
      terminalRef.current = null;
      setError(
        connectError instanceof Error
          ? connectError.message
          : String(connectError),
      );
    } finally {
      setConnecting(false);
    }
  }, [api, cluster, container, disconnect, flushInput, namespace, pod]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      resizeObserverRef.current?.disconnect();
      const session = sessionRef.current;
      if (session) {
        api.closeExecSession(session.id, session.token).catch(() => undefined);
      }
      terminalRef.current?.dispose();
    },
    [api],
  );

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={connecting || connected || !container}
          onClick={connect}
          sx={{ textTransform: 'none' }}
        >
          {connecting ? <CircularProgress size={15} /> : 'Connect'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!connected}
          onClick={disconnect}
          sx={{ textTransform: 'none' }}
        >
          Disconnect
        </Button>
        <Typography sx={{ color: '#c7c7c7', fontSize: 11.5 }}>
          {connected
            ? `Connected to ${container}`
            : 'Sessions close after 15 minutes idle or 60 minutes total'}
        </Typography>
      </Stack>
      {error && (
        <Typography sx={{ color: '#f0561d', fontSize: 12, mb: 1 }}>
          {error}
        </Typography>
      )}
      <Box
        ref={hostRef}
        sx={{
          height: 420,
          p: 0.5,
          bgcolor: '#141414',
          border: '1px solid #4f5255',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      />
    </Box>
  );
}
